import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check, Server, Cloud, Monitor, Network } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AICapabilities from '../components/ai/AICapabilities';
import { getProduct, deploymentModels } from '../data/productRegistry';
import { useLocalizedProducts, useLocalizedDeployments } from '../hooks/useLocalizedProducts';

const deploymentIcon: Record<string, React.ComponentType<{ className?: string }>> = {
  'sovereign-cloud': Cloud,
  'on-premise': Server,
  'standalone': Monitor,
  'saas': Network,
};

const ProductDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation('editor');
  const localizedProducts = useLocalizedProducts();
  const localizedDeployments = useLocalizedDeployments();
  const product = slug ? getProduct(slug) : undefined;
  const loc = localizedProducts.find((p) => p.slug === slug);

  if (!product || !loc) {
    return <Navigate to="/products" replace />;
  }

  const Icon = product.icon;
  const { name, description, tagline } = loc;
  const highlights = Array.isArray(loc.highlights) ? loc.highlights : product.highlights;
  const features = Array.isArray(loc.features) ? loc.features : product.features;

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar />
      </div>

      <section className="relative pt-44 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark mask-fade-b" />
        <div className={`pointer-events-none absolute -top-32 -left-20 w-[620px] h-[620px] rounded-full bg-gradient-to-br ${product.accent} opacity-30 blur-[130px] animate-aurora`} />
        <div className="pointer-events-none absolute top-40 right-[-10%] w-[480px] h-[480px] rounded-full bg-cyan-500/15 blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm font-medium mb-10 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('productPage.allProducts')}
          </Link>

          <div className="grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-3 mb-7">
                <div className={`relative p-3.5 rounded-2xl bg-gradient-to-br ${product.accent} shadow-[0_0_40px_-6px_rgba(34,211,238,0.5)]`}>
                  <Icon className="h-7 w-7 text-white" />
                </div>
                {product.badge && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-cyan-200 border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-pulse" />
                    {product.badge}
                  </span>
                )}
              </div>
              <h1 className="font-display text-5xl lg:text-6xl font-bold text-white mb-4 leading-[1.05]">{name}</h1>
              <p className="text-xl font-medium text-gradient animate-shimmer mb-6">{tagline}</p>
              <p className="text-lg text-slate-300/90 leading-relaxed mb-10 max-w-xl">{description}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-semibold text-slate-950 bg-gradient-to-r from-cyan-300 via-sky-400 to-emerald-300 shadow-[0_0_40px_-8px_rgba(34,211,238,0.8)] hover:shadow-[0_0_56px_-6px_rgba(34,211,238,1)] transition-shadow"
                >
                  {t('productPage.requestDemo')}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-semibold text-white border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
                >
                  {t('productPage.seePricing')}
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="relative"
            >
              <div className={`absolute -inset-4 rounded-[2rem] bg-gradient-to-br ${product.accent} opacity-20 blur-2xl`} />
              <div className="relative grid grid-cols-2 gap-4">
                {product.stats.map((s, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 + i * 0.08 }}
                    className="glow-ring glass-card rounded-3xl p-6 hover:-translate-y-1 transition-transform duration-300"
                  >
                    <div className="font-display text-4xl font-bold text-white">{s.value}</div>
                    <div className="text-sm text-slate-400 mt-2">{s.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {highlights.length > 0 && (
            <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {highlights.map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4"
                >
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-cyan-300 to-emerald-300 flex items-center justify-center">
                    <Check className="h-3.5 w-3.5 text-slate-950" />
                  </span>
                  <p className="text-sm font-medium text-slate-200">{h}</p>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-slate-900 mb-4">{t('productPage.features')}</h2>
            <p className="text-lg text-slate-500">{t('productPage.featuresSubtitle', { name })}</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 3) * 0.06 }}
                className="group relative rounded-3xl p-7 bg-slate-50 border border-slate-200/70 hover:bg-white hover:border-transparent hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.35)] transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${product.accent} flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-display text-sm font-bold text-slate-300 group-hover:text-slate-400 transition-colors">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50 border-t border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-slate-900 mb-4">{t('productPage.deployment')}</h2>
            <p className="text-lg text-slate-500">{t('productPage.deploymentSubtitle', { name })}</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.deployments.map((depId) => {
              const dep = deploymentModels.find((d) => d.id === depId);
              const locDep = localizedDeployments.find((d) => d.id === depId);
              if (!dep || !locDep) return null;
              const DepIcon = deploymentIcon[dep.id] ?? Server;
              const depFeatures = Array.isArray(locDep.features) ? locDep.features : dep.features;
              return (
                <motion.div
                  key={dep.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="rounded-3xl bg-white border border-slate-200/70 p-7 hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.35)] transition-shadow"
                >
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${dep.accent} flex items-center justify-center mb-5 shadow-lg`}>
                    <DepIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{locDep.name}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5">{locDep.description}</p>
                  <ul className="space-y-2">
                    {depFeatures.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <AICapabilities accent={product.accent} productName={name} />

      <section className="relative py-28 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-60" />
        <div className={`pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[360px] rounded-full bg-gradient-to-r ${product.accent} opacity-30 blur-[100px]`} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-[2rem] p-px bg-gradient-to-br from-cyan-300/50 via-white/10 to-emerald-300/40"
          >
            <div className="rounded-[2rem] bg-slate-950/80 backdrop-blur-xl px-8 py-14 sm:px-14 text-center">
              <h2 className="font-display text-4xl lg:text-5xl font-bold text-white mb-5">{t('productPage.ready', { name })}</h2>
              <p className="text-lg text-slate-200 mb-2">{loc.pricingNote}</p>
              <p className="text-base text-slate-400 mb-10">{t('productPage.sovereign')}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-slate-950 bg-gradient-to-r from-cyan-300 via-sky-400 to-emerald-300 shadow-[0_0_40px_-8px_rgba(34,211,238,0.8)] hover:shadow-[0_0_56px_-6px_rgba(34,211,238,1)] transition-shadow"
                >
                  {t('productPage.requestDemo')}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-white border border-white/20 bg-white/[0.04] hover:bg-white/[0.1] transition-colors"
                >
                  {t('productPage.seePricing')}
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
